import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useEndDeleteontract } from '@/modules/workforce/Contracts/apiHooks';
import { Button, Typography } from '@/shared/components/atoms';
import routes from '@/shared/constant/routes';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import Tick from '~/images/shared/CheckMarkWithoutBG.svg';
import CloseSquare from '~/images/shared/CloseSquare.svg';
import Slash from '~/images/shared/Slash.svg';

const EndDeleteContract: React.FC<{
  supplierAssignmentId: number;
  status: 'Active' | 'Upcoming';
  redirectOnSuccess?: boolean;
}> = ({ supplierAssignmentId, status, redirectOnSuccess = true }) => {
  const { t } = useTranslation();

  const { push } = useRouter();

  const [selectedAsset] = useStore((store) => store?.asset);

  const { closeModal } = useModal();

  const queryClient = useQueryClient();

  const endContractVariants = {
    Active: {
      icon: (
        <span className='text-error-20'>
          <Slash width='35' height='35' />
        </span>
      ),
      title: t('Are you sure to end contract?'),
      action: t('End contract'),
      success: t('Contract ended successfully'),
    },
    Upcoming: {
      icon: (
        <span className='text-error-20'>
          <CloseSquare width='35' height='35' />
        </span>
      ),
      title: t('Are you sure to delete contract?'),
      action: t('Delete contract'),
      success: t('Contract deleted successfully'),
    },
  };

  const { mutate, isSuccess, isLoading } = useEndDeleteontract();

  const handleDelete = () => {
    if (selectedAsset)
      mutate({
        entityId: selectedAsset.id,
        supplierAssignmentId: supplierAssignmentId,
        method: status === 'Upcoming' ? 'delete' : 'patch',
        todayDate: format(new Date(), 'yyyy-MM-dd'),
      });
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isSuccess) {
      if (redirectOnSuccess)
        push({
          pathname: routes.contracts,
          query: {
            'contract-type': status === 'Active' ? 1 : 2,
          },
        });
      else queryClient.invalidateQueries(['getContractsList']);
      timeout = setTimeout(() => {
        closeModal();
      }, 10000);
    }
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <div className='flex flex-col items-center gap-32 text-center'>
      {isSuccess ? (
        <>
          <div className=' flex h-64 w-64 items-center justify-center rounded-full bg-secondary-90 text-green-300'>
            <Tick width='40' height='40' />
          </div>
          <Typography variant='h2' weight='medium' className='px-32 pb-24'>
            {endContractVariants[status].success}
          </Typography>
        </>
      ) : (
        <>
          <div className=' flex h-64 w-64 items-center justify-center rounded-full bg-error-90'>
            {endContractVariants[status].icon}
          </div>
          <div>
            <Typography variant='h2' weight='medium'>
              {endContractVariants[status].title}
            </Typography>
            {status === 'Upcoming' && (
              <Typography className='mx-auto mt-8 max-w-[356px] text-surface-10'>
                {t(
                  'You will not be able undo this step. This Feature permanently removes data from a system, ensuring it cannot be recovered'
                )}
              </Typography>
            )}
          </div>
          <div className='flex items-center gap-8'>
            <Button
              onClick={() => closeModal()}
              disabled={isLoading}
              variant='text'
              className=' h-56 !rounded-xl border-xs  border-outline-default'
            >
              {t('Dismiss')}
            </Button>
            <Button
              disabled={isLoading}
              onClick={handleDelete}
              className='h-56 !rounded-xl'
              color='red'
            >
              {endContractVariants[status].action}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default EndDeleteContract;
